import axios from 'axios';
import { notification } from 'antd';
import { get, cloneDeep } from 'lodash';
import * as chatTypes from './types/chat.types.js';
import { UnauthorizedHandler } from '../js/helper/UnauthorizedHandler.js';
import { getTeamsByWorkspaceId } from './teamAction.js';
import { getChatRooms } from './chatRoomAction.js';
import { getWorkspaceNotifications } from './../redux/state-areas/notifications/actions';
import {
  clearOpenedTaskId,
  getUserTasksAction,
  setUserTasksFiltersAction,
} from '../redux/tasks/actions.js';
import TASKS_SCHEMAS from '../redux/tasks/schemas';
import { normalize } from 'normalizr';
import { ACTION_TYPES as TASKS_ACTION_TYPES } from '../redux/tasks/constants';
import { ACTION_TYPES as PROJECTS_ACTION_TYPES } from '../redux/projects/constants.js';

const baseUrl = process.env.REACT_APP_API_URL;

const config = {
  headers: { Authorization: '' },
};

let lastWorkspacesFetchTime = 0;
const WORKSPACES_CACHE_TTL = 30000; // 30 seconds

export const getWorkspaces = (response) => {
  return async (dispatch, getState) => {
    try {
      // Skip fetch if workspaces were fetched recently
      const now = Date.now();
      const existingWorkspaces = getState().workspaceReducer?.workspaces;
      if (existingWorkspaces?.length > 0 && (now - lastWorkspacesFetchTime) < WORKSPACES_CACHE_TTL) {
        return existingWorkspaces;
      }

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const queryString = response ? `?response=${response}` : '';
      const res = await axios.get(
        `${baseUrl}/workspaces${queryString}`,
        config,
      );
      lastWorkspacesFetchTime = now;
      dispatch({
        type: 'get-workspaces',
        payload: res?.data?.data,
      });
      return res?.data?.data;
    } catch (error) {
      UnauthorizedHandler(dispatch, error);
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const getActiveWorkspaceDetails = () => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const workspaceId = sessionStorage.getItem('workspaceId');
      const res = await axios.get(
        `${baseUrl}/workspaces/${workspaceId}/details`,
        config,
      );
      dispatch({
        type: 'set-current-workspace',
        payload: res?.data?.data,
      });
    } catch (error) {
      UnauthorizedHandler(dispatch, error);
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

// --- Normalized-wire rehydration (opt-in via `projects.payload === 'norm-v1'`) ---
// Server ships participants as {user, role, limitedAccess} tuples and custom
// fields as ids + a deduplicated table; this rebuilds today's exact shape
// before anything reaches Redux. Gated server-side by DASHBOARD_NORM_PAYLOAD;
// off => byte-identical legacy response, passed through untouched.

// Thrown only for a structural fault (customFieldIds without customFieldDefs) —
// never for an orphan participant or dangling link, which are normal and must
// reproduce today's output rather than retry.
class DashboardStructuralFaultError extends Error {}

const projectListsCarryCustomFieldIds = (data) =>
  [...(data?.allProjects || []), ...(data?.myProjects || [])].some((p) =>
    Array.isArray(p?.customFieldIds),
  );

// Built from `participantDefs` only — deliberately no fallback to
// members/deactivated/deleted, since a participant need not be a workspace
// member and that fallback previously hid exactly that bug (F-001).
const buildWorkspaceUserDirectory = (data) => {
  const directory = new Map();
  (data?.participantDefs || []).forEach((u) => {
    const id = u?._id != null ? String(u._id) : undefined;
    if (!id) return;
    directory.set(id, {
      _id: u._id,
      fullName: u.fullName,
      avi: u.avi,
      email: u.email,
    });
  });
  return directory;
};

// Explicit fields, not a spread — spreading would leak keys today's shape
// doesn't have. Unresolvable user reproduces legacy's output:
// {role, limitedAccess} only, not an error.
const rebuildParticipant = (tuple, directory) => {
  const userId = tuple?.user != null ? String(tuple.user) : undefined;
  const dirUser = userId ? directory.get(userId) : undefined;
  if (!dirUser) {
    return {
      role: tuple?.role,
      limitedAccess: tuple?.limitedAccess,
    };
  }
  return {
    _id: dirUser._id,
    fullName: dirUser.fullName,
    avi: dirUser.avi,
    email: dirUser.email,
    role: tuple?.role,
    limitedAccess: tuple?.limitedAccess,
  };
};

// Dangling id -> null, matching legacy. Deep-cloned per occurrence: one
// definition can link to several projects, and sharing the object would let
// an in-place sort in one project corrupt another.
const rebuildCustomFields = (customFieldIds, defsById) =>
  (customFieldIds || []).map((id) => {
    const def = id != null ? defsById.get(String(id)) : undefined;
    return def ? cloneDeep(def) : null;
  });

const rebuildProject = (project, directory, defsById) => {
  const { participants, customFieldIds, ...rest } = project || {};
  return {
    ...rest,
    participants: (participants || []).map((tuple) =>
      rebuildParticipant(tuple, directory),
    ),
    customFields: rebuildCustomFields(customFieldIds, defsById),
  };
};

// Rebuilt separately and never memoized by id, so a project in both lists
// gets independently allocated data — no shared references (C-001).
const rebuildProjectList = (projects, directory, defsById) =>
  (projects || []).map((project) => rebuildProject(project, directory, defsById));

// customFieldDefs present => normalized response; absent => legacy (keeps a
// new client working against an old server). Throws only per the fault above.
const rehydrateDashboardPayload = (data) => {
  if (!data) return data;
  const isNormalized = Array.isArray(data.customFieldDefs);
  if (!isNormalized) {
    if (projectListsCarryCustomFieldIds(data)) {
      throw new DashboardStructuralFaultError(
        'customFieldIds present without customFieldDefs',
      );
    }
    return data;
  }

  // participantDefs always accompanies customFieldDefs; missing it means a
  // malformed payload, not "no participants" — continuing would silently
  // strip every name/avatar. Same structural-fault treatment as above.
  if (!Array.isArray(data.participantDefs)) {
    throw new DashboardStructuralFaultError(
      'customFieldDefs present without participantDefs',
    );
  }

  const directory = buildWorkspaceUserDirectory(data);
  const defsById = new Map(
    (data.customFieldDefs || []).map((d) => [String(d?._id), d]),
  );

  // Strip both tables before returning — they're wire scaffolding, already
  // rebuilt into each project, and must not reach Redux (workspaceReducer
  // stores the payload verbatim; a legacy response never had these keys).
  const { customFieldDefs, participantDefs, ...rest } = data;

  return {
    ...rest,
    allProjects: rebuildProjectList(data.allProjects, directory, defsById),
    myProjects: rebuildProjectList(data.myProjects, directory, defsById),
  };
};

export const getWorkspaceDashboardData = (assignedTo, dueDate) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const workspaceId = localStorage.getItem('workspaceId');
      const dashboardUrl = `${baseUrl}/workspaces/${workspaceId}/dashboard`;
      const buildRequestBody = (useNormalizedPayload) => ({
        charts: {
          filters: {
            assignedTo: assignedTo,
            dueDate: dueDate,
          },
        },
        ...(useNormalizedPayload ? { projects: { payload: 'norm-v1' } } : {}),
      });

      let res = await axios.post(dashboardUrl, buildRequestBody(true), config);

      let hydrated;
      try {
        hydrated = rehydrateDashboardPayload(res?.data?.data);
      } catch (err) {
        if (!(err instanceof DashboardStructuralFaultError)) throw err;
        // One-shot retry without the flag, reserved for a structural fault —
        // orphans and dangling links are not errors and must not retry.
        res = await axios.post(dashboardUrl, buildRequestBody(false), config);
        hydrated = res?.data?.data;
      }

      // old code but not breaking it
      let tasks = [];

      dispatch({
        type: 'dashboard-loading',
        payload: true,
      });

      dispatch({
        type: 'workspaceDashboard',
        payload: hydrated,
      });

      const workspace = hydrated?.workspace;
      const organization = hydrated?.organization;
      const members = hydrated?.members || [];
      const deactivated = hydrated?.deactivated || [];
      const pendingInvites = hydrated?.pendingInvites || [];
      const workspaceTasks = hydrated?.workspaceTasks || [];

      const normalizedWorkspaceTasks = normalize(
        { ...workspaceTasks, tasks },
        TASKS_SCHEMAS.GET_WORKSPACE_TASKS_SCHEMA,
      );

      // console.log(normalizedWorkspaceTasks);
      const allProjects = hydrated?.allProjects || [];
      const myProjects = hydrated?.myProjects || [];
      const customFields = hydrated?.customFields || [];
      const charts = hydrated?.charts || {};
      const teams = hydrated?.teams || [];
      const chatRooms = hydrated?.chatRooms || [];
      const tags = hydrated?.tags || [];
      const pinned = hydrated?.pinned || [];

      dispatch({
        type: 'set-current-workspace',
        payload: {
          ...workspace,
          organization,
          members,
          pendingInvites,
          projects: allProjects,
          myProjects,
          deactivated,
        },
      });

      dispatch({
        type: 'set-workspace-role',
        payload: members
      });

      dispatch({
        type: 'set-workspace-fields',
        payload: customFields,
      });

      dispatch({
        type: 'set-pinned-items',
        payload: pinned,
      });

      dispatch({
        type: TASKS_ACTION_TYPES.GET_DASHBOARD_TASKS_REQUEST_SUCCESS,
        payload: {
          assignees: normalizedWorkspaceTasks?.entities?.assignees,
          users: normalizedWorkspaceTasks?.entities?.users,
          tasks: normalizedWorkspaceTasks?.entities?.tasks,
          tasksIdList: normalizedWorkspaceTasks?.result?.tasks,
          completedTasks: workspaceTasks?.completedTasks,
          totalTasks: workspaceTasks?.totalTasks,
          unassignedTasks: workspaceTasks?.unassignedTasks,
        },
      });
      dispatch({
        type: 'workspace-projects',
        payload: allProjects,
      });
      dispatch({
        type: PROJECTS_ACTION_TYPES.GET_USER_PROJECTS_REQUEST_SUCCESS,//'get-users-projects',
        payload: myProjects,
      });
      dispatch({
        type: 'success-get',
        payload: members,
      });
      dispatch({
        type: 'workspaceCharts',
        payload: charts,
      });
      dispatch({
        type: 'dashboard-loading',
        payload: false,
      });
      dispatch({
        type: 'get-teams',
        payload: teams,
      });
      dispatch({
        type: chatTypes.GET_CHAT_SUCCESS,
        payload: chatRooms,
      });
      dispatch({
        type: 'get-workspace',
        payload: workspace,
      });
      dispatch({
        type: 'getWorkspaceTags',
        payload: tags || [],
      });
    } catch (error) {
      UnauthorizedHandler(dispatch, error);
      dispatch({
        type: 'error',
        payload: error,
      });
      dispatch({
        type: 'dashboard-loading',
        payload: false,
      });
    }
  };
};

export const getWorkspaceProjects = (wsId) => {
  return async (dispatch) => {
    try {
      const workspaceId =
        wsId ||
        sessionStorage.getItem('workspaceId') ||
        localStorage.getItem('workspaceId');
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/workspaces/${workspaceId}/projects`,
        config,
      );
      dispatch({
        type: 'workspace-projects',
        payload: res?.data?.data,
      });
      dispatch({
        type: PROJECTS_ACTION_TYPES.GET_USER_PROJECTS_REQUEST_SUCCESS,//'get-users-projects',
        payload: res?.data?.data,
      });

      return res?.data?.data;
    } catch (error) {
      UnauthorizedHandler(dispatch, error);
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

let lastWorkspaceByIdFetchTime = 0;
const WORKSPACE_BYID_CACHE_TTL = 10000; // 10 seconds

export const getWorkspaceById = (callBack) => {
  return async (dispatch, getState) => {
    try {
      const workspaceId =
        sessionStorage.getItem('workspaceId') ||
        localStorage.getItem('workspaceId');

      // Skip fetch if workspace was fetched very recently (dedup rapid calls)
      const now = Date.now();
      const existingWorkspace = getState().workspaceReducer?.currentWorkspace;
      if (existingWorkspace?._id === workspaceId && (now - lastWorkspaceByIdFetchTime) < WORKSPACE_BYID_CACHE_TTL) {
        callBack && callBack(existingWorkspace);
        return;
      }

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/workspaces/${workspaceId}`,
        config,
      );
      lastWorkspaceByIdFetchTime = now;
      dispatch({
        type: 'get-workspace',
        payload: res?.data?.data?.workspace,
      });
      dispatch({
        type: 'set-workspace-fields',
        payload: res?.data?.data?.customFields,
      });
      dispatch({
        type: chatTypes.GET_CHAT_SUCCESS,
        payload: res?.data?.data?.chatRooms,
      });
      dispatch({
        type: 'get-teams',
        payload: res?.data?.data?.teams,
      });
      callBack && callBack(res?.data?.data?.workspace);
    } catch (error) {
      console.log('----- error', error);
      UnauthorizedHandler(dispatch, error);
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const addWorkspace = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/workspaces`, data, config);

      const { invitees, title } = data;

      await dispatch({
        type: 'workspace-success',
        payload: res?.data?.data,
      });
      notification.success({
        message: title ? `${title} created` : 'Workspace created',
        placement: 'bottomRight',
      });
      invitees &&
        invitees.length > 0 &&
        notification.success({
          message: 'Email invitations sent to team members',
          placement: 'bottomRight',
        });
      callback && callback(res?.data?.data);
      return res?.data?.data;
    } catch (error) {
      const isDuplicate = get(error, 'response.data.isDuplicateField');
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: isDuplicate
          ? 'Workspace title already used'
          : error?.response?.data?.message,
        placement: 'bottomRight',
      });
    }
  };
};

export const deleteWorkspace = (id) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.delete(`${baseUrl}/workspaces/${id}`, config);
      dispatch({
        type: 'workspace-delete',
        payload: id,
      });
      notification.success({
        message: 'You just deleted a workspace',
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
      notification.error({
        message: 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const switchWorkspace = (workspace, callback) => {
  return async (dispatch) => {
    try {
      dispatch({
        type: 'set-current-workspace',
        payload: workspace,
      });
      dispatch({
        type: TASKS_ACTION_TYPES.CLEAR_USER_TASKS,
      });
      callback && callback();
    } catch (e) {
      UnauthorizedHandler(dispatch, e);
      notification.error({
        message: 'Cannot switch workspace, please try again!',
        placement: 'bottomRight',
      });
    }
  };
};

export const inviteToWorkspace = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/workspaces/invite`,
        data,
        config,
      );
      callback && callback();
      notification.success({
        message:
          res?.data?.message || 'Email invitations sent to workspace members',
        placement: 'bottomRight',
      });
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || 'Cannot invite to workspace!',
        placement: 'bottomRight',
      });
    }
  };
};

export const generateWorkspaceInviteLink = (workspaceId, data, callback) => {
  return async () => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/workspace/${workspaceId}/magic-link`,
        data,
        config,
      );
      callback && callback(res?.data?.data);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || 'Failed to generate invite link',
        placement: 'bottomRight',
      });
      callback && callback(null);
    }
  };
};

export const joinWorkspaceViaLink = (token, callback) => {
  return async () => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/join/${token}`,
        {},
        config,
      );
      callback && callback(null, res?.data?.data);
    } catch (e) {
      callback && callback(e?.response?.data?.message || 'Failed to join workspace');
    }
  };
};

export const getWorkspaceInviteLinks = (workspaceId, callback) => {
  return async () => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/workspace/${workspaceId}/magic-link`,
        config,
      );
      callback && callback(res?.data?.data);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || 'Failed to fetch invite links',
        placement: 'bottomRight',
      });
      callback && callback(null);
    }
  };
};

export const revokeWorkspaceInviteLink = (workspaceId, token, callback) => {
  return async () => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.patch(
        `${baseUrl}/workspace/${workspaceId}/magic-link/${token}/revoke`,
        {},
        config,
      );
      notification.success({
        message: 'Invite link revoked',
        placement: 'bottomRight',
      });
      callback && callback();
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || 'Failed to revoke invite link',
        placement: 'bottomRight',
      });
    }
  };
};

export const updateWorkspaceUsers = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(
        `${baseUrl}/workspaces/updateWorkspaceUsers`,
        data,
        config,
      );
      callback && callback();
      notification.success({
        message: 'User roles have been updated!',
        placement: 'bottomRight',
      });
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || 'Cannot update role!',
        placement: 'bottomRight',
      });
    }
  };
};

export const acceptWorkspaceInvite = (inviteId, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/workspaces/invite/${inviteId}/accept`,
        {},
        config,
      );
      dispatch({
        type: 'workspace-remove-invite',
        payload: inviteId,
      });
      const workspace = res?.data?.data;
      dispatch(
        switchWorkspace(workspace, () => {
          sessionStorage.setItem('workspaceId', workspace._id);
          sessionStorage.setItem('organization', workspace.organization);

          localStorage.setItem('workspaceId', workspace._id);
          localStorage.setItem('organization', workspace.organization);

          dispatch(getTeamsByWorkspaceId(workspace._id));
          // dispatch(getChatRooms())
          dispatch(getWorkspaceById());
          dispatch(getUserTasksAction());
          dispatch(getWorkspaceNotifications());
          dispatch(clearOpenedTaskId());
          dispatch(setUserTasksFiltersAction({}, true));
        }),
      );
      callback && callback();
      return workspace;
    } catch (error) {
      UnauthorizedHandler(dispatch, error);
      dispatch({
        type: 'error',
        payload: error,
      });
      notification.error({
        message: 'Unable to accept workspace invite',
        placement: 'bottomRight',
      });
    }
  };
};

export const declineWorkspaceInvite = (inviteId, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(
        `${baseUrl}/workspaces/invite/${inviteId}/decline`,
        {},
        config,
      );
      callback && callback();
      notification.success({
        message: 'Declined invite!',
        placement: 'bottomRight',
      });
      dispatch({
        type: 'workspace-remove-invite',
        payload: inviteId,
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
      notification.error({
        message: 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const getWorkspaceInvites = () => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/user/workspaceInvites/pending`,
        config,
      );
      dispatch({
        type: 'get-workspace-invites',
        payload: res?.data?.data,
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

let searchWorkspaceController = null;

export const searchWorkspace = (query, context = 'both') => {
  return async (dispatch) => {
    // Cancel previous in-flight search to prevent race conditions
    if (searchWorkspaceController) {
      searchWorkspaceController.abort();
    }
    searchWorkspaceController = new AbortController();

    try {
      // Dispatch loading start
      dispatch({
        type: 'search-workspace-loading',
        payload: true,
      });

      const workspaceId = sessionStorage.getItem('workspaceId');
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/workspaces/${workspaceId}/search?query=${query}`,
        { ...config, signal: searchWorkspaceController.signal },
      );

      if (context === 'page' || context === 'both') {
        dispatch({
          type: 'search-workspace-page',
          payload: res?.data?.data,
        });
      }

      if (context === 'popover' || context === 'both') {
        dispatch({
          type: 'search-workspace-popover',
          payload: res?.data?.data,
        });
      }

      const tasks = res?.data?.data?.tasks || res?.data?.data || [];

      const normalizedTasksResp = normalize(
        { tasks: Array.isArray(tasks) ? tasks : [tasks] },
        TASKS_SCHEMAS.GET_ALL_TASKS_SCHEMA,
      );

      dispatch({
        type: TASKS_ACTION_TYPES.SEARCH_TASKS_REQUEST_SUCCESS,
        payload: {
          tasks: normalizedTasksResp?.entities?.tasks || {},
          assignees: normalizedTasksResp?.entities?.assignees || {},
          users: normalizedTasksResp?.entities?.users || {},
          tasksIdList: normalizedTasksResp?.result?.tasks || [],
          count:
            res?.data?.data?.count ||
            (Array.isArray(tasks) ? tasks.length : 1),
          page: 1,
        },
      });

      // Dispatch loading end
      dispatch({
        type: 'search-workspace-loading',
        payload: false,
      });

      return res?.data?.data;
    } catch (error) {
      if (axios.isCancel(error)) return; // Aborted — ignore
      // Dispatch loading end on error
      dispatch({
        type: 'search-workspace-loading',
        payload: false,
      });

      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const getWorkspaceDefaultFilter = (workspaceId) => {
  return async (dispatch) => {
    try {
      if (!workspaceId) {
        workspaceId =
          sessionStorage.getItem('workspaceId') ||
          localStorage.getItem('workspaceId');
      }

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/filter/workspace/${workspaceId}`,
        config,
      );
      dispatch({
        type: 'workspaceFilter',
        payload: res?.data?.data || {},
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'workspaceFilter',
        payload: {},
      });
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const removeMemberFromWorkspace = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(`${baseUrl}/workspaces/removeMembers`, data, config);
      dispatch({
        type: 'remove-workspace-user',
        payload: data?.members?.[0] || '',
      });
      callback && callback();
      notification.success({
        message: 'Member removed from workspace!',
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const getWorkspaceTags = (workspaceId) => {
  return async (dispatch) => {
    try {
      if (!workspaceId) {
        workspaceId =
          sessionStorage.getItem('workspaceId') ||
          localStorage.getItem('workspaceId');
      }

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/workspaces/${workspaceId}/tags`,
        config,
      );
      dispatch({
        type: 'getWorkspaceTags',
        payload: res?.data?.data || {},
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const addWorkspaceTag = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/workspaces/tags`, data, config);
      const addedTag = res?.data?.data;
      dispatch({
        type: 'add-workspace-tag',
        payload: addedTag,
      });
      callback && callback(addedTag);
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const updateWorkspaceTag = (tagId, data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.patch(`${baseUrl}/workspaces/tags/${tagId}`, data, config);
      const updatedTag = res?.data?.data;
      dispatch({ type: 'update-workspace-tag', payload: updatedTag });
      callback && callback(updatedTag);
    } catch (error) {
      dispatch({ type: 'error', payload: error });
    }
  };
};

export const removeWorkspaceTag = (tag, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.delete(`${baseUrl}/workspaces/tags/${tag}`, config);
      dispatch({
        type: 'remove-workspace-tag',
        payload: tag,
      });
      callback && callback();
      notification.success({
        message: 'Tag removed from workspace!',
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const getPortfolios = () => {
  return async (dispatch) => {
    try {
      const workspaceId =
        sessionStorage.getItem('workspaceId') ||
        localStorage.getItem('workspaceId');

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/portfolio/workspace/${workspaceId}`,
        config,
      );
      dispatch({
        type: 'set-portfolios',
        payload: res?.data?.data || {},
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
}

export const createPortfolio = (data) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/portfolio`, data, config);
      dispatch({
        type: 'add-portfolios',
        payload: res?.data?.data || {},
      });
      notification.success({
        message: 'Portfolio Created!',
        placement: 'bottomRight',
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
}

export const getPortfolio = (portfolioId) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/portfolio/${portfolioId}`,
        config,
      );
      dispatch({
        type: 'set-portfolio',
        payload: res?.data?.data || {},
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
}

export const deletePortfolio = (portfolioId) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.delete(
        `${baseUrl}/portfolio/${portfolioId}`,
        config,
      );
      dispatch({
        type: 'delete-portfolio',
        payload: portfolioId
      });
      dispatch({
        type: 'delete-portfolio-pinned',
        payload: portfolioId
      });
      notification.success({
        message: 'Portfolio Deleted!',
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
}

export const removeProjectFromPortfolio = (portfolioId, projects) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(
        `${baseUrl}/portfolio/${portfolioId}/project/remove`,
        { projects: projects },
        config,
      );
      await dispatch({
        type: 'remove-project-portfolio',
        payload: {
          portfolioId: portfolioId,
          projects: projects
        }
      });
      await dispatch({
        type: 'delete-pin-portfolio-projects',
        payload: {
          portfolioId,
          projects,
        },
      });
      notification.success({
        message: 'Projects Removed!',
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
}

export const addProjectFromPortfolio = (portfolioId, projects) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(
        `${baseUrl}/portfolio/${portfolioId}/project/add`,
        { projects: projects },
        config,
      );
      await dispatch({
        type: 'add-project-portfolio',
        payload: {
          portfolioId,
          projects
        }
      });
      await dispatch({
        type: 'update-pin-portfolio-data',
        payload: {
          portfolioId,
          projects,
        },
      });
      return;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
}

export const addPortfolioMembers = (portfolioId, users) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(
        `${baseUrl}/portfolio/${portfolioId}/users/add`,
        { users },
        config,
      );
      dispatch({
        type: 'add-portfolio-members',
        payload: users.map(u => ({ _id: u.userId, fullName: u.fullName, avi: u.avi, email: u.email, role: u.role })),
      });
    } catch (error) {
      dispatch({ type: 'error', payload: error });
    }
  };
};

export const removePortfolioMember = (portfolioId, userId) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(
        `${baseUrl}/portfolio/${portfolioId}/users/remove`,
        { users: [{ userId }] },
        config,
      );
      dispatch({
        type: 'remove-portfolio-member',
        payload: userId,
      });
    } catch (error) {
      dispatch({ type: 'error', payload: error });
    }
  };
};

export const updateProjectFromPortfolio = (portfolioId, data) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.put(
        `${baseUrl}/portfolio/${portfolioId}`,
        data,
        config,
      );

      await dispatch({
        type: "update-portfolio-data",
        payload: { portfolioId, name: data?.name, access: data?.access },
      });

      await dispatch({
        type: 'update-pin-portfolio-data',
        payload: { portfolioId, name: data?.name, access: data?.access }
      });

      return;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
}
